import Navbar from "../Navbar"
import Footer from "../Footer"
import { motion, useScroll, useTransform } from "framer-motion";
import eiReceipt from "../../../static/images/services/ei_receipt.jpg";

const ConditionSurveyImportExport = () => {
  const fadeUp = {
    hidden: { opacity: 0, y: 60 },
    show: { opacity: 1, y: 0 },
  };
  const features = [
    {
      title: "Pre-Shipment Inspections",
      description: "Complete inspection of containers and cargo before shipment to document initial condition.",
    },
    {
      title: "Damage Assessment",
      description: "Expert identification and assessment of any damage with detailed reporting.",
    },
    {
      title: "Photographic Evidence",
      description: "Professional photographs documenting all aspects of cargo and container condition.",
    },
    {
      title: "Port Expertise",
      description: "Specialized knowledge of Mumbai Port and JNPT operations and procedures.",
    },
    {
      title: "Compliance Verification",
      description: "Ensures full compliance with international shipping standards and regulations.",
    },
    {
      title: "Rapid Reporting",
      description: "Quick turnaround on detailed condition reports for port operations.",
    },
  ]

  const benefits = [
    "Protects cargo value",
    "Establishes claim documentation",
    "Reduces shipping disputes",
    "Ensures regulatory compliance",
    "Professional expertise",
    "Fast certification",
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[60vh] mt-20 overflow-hidden">
        <img
          src={eiReceipt}
          alt="Condition Survey Import Export"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-black-800/90 to-transparent">
          <div className="container mx-auto px-4 h-full flex items-center">
            <div className="max-w-3xl text-white">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Condition Survey Import/Export</h1>
              <p className="text-xl text-blue-100">
                Detailed condition surveys during import and export operations from Mumbai Port and JNPT
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Overview */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-semibold text-orange mb-6">Service Overview</h2>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              Our condition survey services for import and export operations provide comprehensive documentation of
              cargo and container condition at critical points in your supply chain. Working extensively with Mumbai
              Port and JNPT, we offer specialized expertise in port operations.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              Our surveyors document initial conditions, identify any damage, and provide professional photographic
              evidence. This documentation is essential for claims handling, quality assurance, and regulatory
              compliance across international shipments.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Key Features */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-20 bg-gray-50 pt-14">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-semibold text-orange mb-6 text-center">Key Features</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all">
                <h3 className="text-xl font-semibold text-gray-700 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Benefits Section */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-20 bg-white pt-14">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-semibold text-orange mb-12 text-center">Why Choose Our Service</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <svg className="w-6 h-6 text-orange mt-1" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <span className="text-lg text-gray-700">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.section>

      {/* Process Section */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-20 bg-gray-50 pt-14">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-semibold text-orange mb-12 text-center">Our Process</h2>
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8 mx-auto">
              {[
                {
                  num: "1",
                  title: "Schedule Survey",
                  desc: "Contact us to arrange survey timing aligned with your port operations",
                },
                {
                  num: "2",
                  title: "On-Site Inspection",
                  desc: "Our surveyors conduct detailed inspection of cargo and containers at the port",
                },
                {
                  num: "3",
                  title: "Documentation",
                  desc: "Professional photographs and measurements documenting all conditions",
                },
                { num: "4", title: "Assessment Report", desc: "Detailed damage assessment and condition evaluation" },
                {
                  num: "5",
                  title: "Report Delivery",
                  desc: "Comprehensive report delivered promptly for claims or records",
                },
              ].map((step, index) => (
                <div key={index} className="flex gap-6">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-orange text-white font-semibold">
                      {step.num}
                    </div>
                  </div>
                  <div className="flex-grow">
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">{step.title}</h3>
                    <p className="text-gray-600">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.section>

      <motion.section
        id="contact"
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
      >
        <Footer />
      </motion.section>
    </div>
  )
}

export default ConditionSurveyImportExport;